import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { AuthMenu } from "@/components/AuthMenu";
import { PostTile } from "@/components/PostTile";
import { useCurrentUser } from "@/hooks/useAuth";
import { useFavoritesPage } from "@/hooks/useUserData";

const PER_PAGE = 40;

export function FavoritesPage() {
  const { data: user, isLoading: authLoading } = useCurrentUser();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useFavoritesPage(page, PER_PAGE);

  if (authLoading) return null;

  if (!user) {
    return (
      <CenterLayout title="Favorites — sign in">
        <p className="text-zinc-400 text-sm">
          Favorites are tied to an account. Sign in to view yours.
        </p>
        <div className="flex gap-2 justify-center pt-2">
          <Link
            to="/login"
            className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded text-sm"
          >
            Log in
          </Link>
          <Link
            to="/register"
            className="bg-surface-2 hover:bg-surface-3 text-zinc-300 px-4 py-2 rounded text-sm"
          >
            Register
          </Link>
        </div>
      </CenterLayout>
    );
  }

  const favorites = data?.favorites ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <>
      <Helmet>
        <title>Favorites — e621 Animation Finder</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex flex-col">
        <div className="sticky top-0 z-30 bg-surface-0/95 backdrop-blur border-b border-surface-2 px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center gap-3 flex-wrap">
            <Link
              to="/"
              className="text-sm text-zinc-400 hover:text-accent-light min-h-[40px] inline-flex items-center"
            >
              ← Search
            </Link>
            <h1 className="text-sm font-semibold">Your favorites</h1>
            <span className="text-xs text-zinc-600">{total.toLocaleString()} saved</span>
            <div className="ml-auto">
              <AuthMenu />
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-6 max-w-6xl mx-auto w-full">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-surface-2 rounded-lg animate-pulse aspect-square" />
              ))}
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">
              <p className="text-lg">No favorites yet</p>
              <p className="text-sm mt-1">
                Tap the heart on any post to save it here.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {favorites.map((entry) => (
                  <PostTile key={entry.post_id} post={entry.post} />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-6">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="text-xs text-zinc-400 hover:text-accent-light disabled:opacity-30 px-4 py-2 min-h-[40px] inline-flex items-center"
                  >
                    ← Prev
                  </button>
                  <span className="text-xs text-zinc-500">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="text-xs text-zinc-400 hover:text-accent-light disabled:opacity-30 px-4 py-2 min-h-[40px] inline-flex items-center"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function CenterLayout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen bg-surface-0 flex items-center justify-center px-6">
        <div className="max-w-sm w-full bg-surface-1 border border-surface-3 rounded-lg p-6 text-center space-y-3">
          <h1 className="text-lg font-semibold">Favorites</h1>
          {children}
        </div>
      </div>
    </>
  );
}
